import React, { useCallback } from 'react';

import { IntlProvider, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import Button from '@atlaskit/button/new';
import Image from '@atlaskit/image';
import { IntlMessagesProvider } from '@atlaskit/intl-messages-provider';
import { Text, Stack } from '@atlaskit/primitives/compiled';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

import IllustrationDark from '../../../assets/ColoredError--dark.svg';
import IllustrationLight from '../../../assets/ColoredError--light.svg';
import {
	defaultMessages,
	fetchMessagesForLocale,
} from '../../../i18n/i18nProvider/fetchLocaleMessages';
import { redirectToLogin } from '../../utils/redirect-utils';
import { ErrorLayout } from '../error-layout';

import messages from './messages';

type AggErrorPageProps = {
	error: {
		message: string;
	};
};

const AggErrorPageInner = ({ error }: AggErrorPageProps): React.JSX.Element => {
	di(ErrorLayout, redirectToLogin);
	const { formatMessage } = useIntl();
	const { colorMode } = useThemeObserver();

	const handleButtonClick = useCallback(() => redirectToLogin(), []);
	return (
		<ErrorLayout
			testId="agg-error-page"
			illustration={
				<Image src={colorMode === 'light' ? IllustrationLight : IllustrationDark} alt="" />
			}
			heading={formatMessage(messages.aggErrorHeading)}
			message={
				<Stack space="space.200" alignBlock="center">
					<Text as="p" size="large">
						{formatMessage(messages.aggErrorMessage)}
					</Text>
					<Text size="small" as="p">
						{formatMessage(messages.aggErrorMessageSubtext)}
					</Text>
				</Stack>
			}
			action={
				<Button testId="agg-error-page-sign-in-button" onClick={handleButtonClick}>
					{formatMessage(messages.aggErrorAction)}
				</Button>
			}
		/>
	);
};

export const AggErrorPage = (props: AggErrorPageProps): React.JSX.Element => {
	return (
		<IntlProvider locale={navigator.language}>
			<IntlMessagesProvider loaderFn={fetchMessagesForLocale} defaultMessages={defaultMessages}>
				<AggErrorPageInner {...props} />
			</IntlMessagesProvider>
		</IntlProvider>
	);
};
